"use client";

/**
 * The engine's depth blocks, rendered.
 *
 * `calculate_panchang` returns calendar reckonings, observance verdicts, and
 * per-nakshatra/per-rashi strength (381 fields against the category leader's 85+),
 * but nothing displayed them — so the depth existed only in the API.
 *
 * Two deliberate choices:
 *
 * - **These sit AFTER the offer** (rule 46). They are optional depth, not the
 *   value moment; putting them above would push the offer below a long stack,
 *   which is precisely the reach failure that rule has repeatedly caught.
 * - **Tarabalam/Chandrabalam render for EVERYONE at once**, grouped by verdict,
 *   rather than behind a "enter your nakshatra" form. The seeker finds their own
 *   row. That keeps one URL serving every visitor (rule 66 #8) and is strictly
 *   more useful than the same calculator gated behind an input.
 *
 * Every value here is engine-computed and passed through as a placeholder — no
 * prose states a computed fact as a literal (rules 24/29).
 */

import type { PanchangResponse } from "@/lib/api/panchang";

type Quality = "favourable" | "mixed" | "unfavourable";

const QUALITY_ORDER: Quality[] = ["favourable", "mixed", "unfavourable"];

const QUALITY_LABEL: Record<Quality, { en: string; hi: string }> = {
  favourable: { en: "Favourable", hi: "अनुकूल" },
  mixed: { en: "Mixed", hi: "मिश्रित" },
  unfavourable: { en: "Avoid", hi: "प्रतिकूल" },
};

function DepthSection({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <details className="card-sacred overflow-hidden">
      <summary className="cursor-pointer px-5 py-4 text-sm font-semibold text-ish-dark">
        {title}
        {subtitle ? <span className="ml-2 font-normal text-ish-faint">{subtitle}</span> : null}
      </summary>
      <div className="border-t border-ish-border px-5 py-4">{children}</div>
    </details>
  );
}

function FactRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-ish-border py-2 last:border-b-0">
      <span className="text-sm text-ish-faint">{label}</span>
      <span className="text-right text-sm font-semibold text-ish-dark">{value}</span>
    </div>
  );
}

function CalendarSystemsPanel({
  systems,
  isHindi,
}: {
  systems: NonNullable<PanchangResponse["calendar_systems"]>;
  isHindi: boolean;
}) {
  return (
    <DepthSection
      title={isHindi ? "संवत और काल गणना" : "Calendar systems"}
      subtitle={isHindi ? systems.samvatsara_hi : systems.samvatsara}
    >
      <div className="mb-4">
        <FactRow
          label={isHindi ? "संवत्सर" : "Samvatsara"}
          value={`${isHindi ? systems.samvatsara_hi : systems.samvatsara} (${systems.samvatsara_number}/60)`}
        />
        <FactRow
          label={isHindi ? "अयन" : "Ayana"}
          value={isHindi ? systems.ayana_hi : systems.ayana}
        />
        <FactRow
          label={isHindi ? "ऋतु" : "Ritu"}
          value={isHindi ? systems.ritu_hi : systems.ritu}
        />
        <FactRow label={isHindi ? "गोल" : "Gola"} value={systems.gola} />
        <FactRow label={isHindi ? "सौर मास" : "Solar month"} value={systems.solar_month} />
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-[420px] border-collapse text-left text-sm">
          <thead>
            <tr className="border-b border-ish-border text-ish-faint">
              <th className="py-2 pr-4 font-medium">{isHindi ? "संवत" : "Era"}</th>
              <th className="py-2 pr-4 font-medium">{isHindi ? "वर्ष" : "Year"}</th>
              <th className="py-2 font-medium">{isHindi ? "नववर्ष" : "New year at"}</th>
            </tr>
          </thead>
          <tbody>
            {systems.eras.map((era) => (
              <tr key={era.era} className="border-b border-ish-border last:border-b-0">
                <td className="py-2 pr-4 font-semibold text-ish-dark">
                  {isHindi ? era.era_hi : era.era}
                </td>
                <td className="py-2 pr-4 text-ish-dark">{era.year}</td>
                <td className="py-2 text-ish-faint">{era.new_year_at}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DepthSection>
  );
}

// Label pairs live as data so the panel is a map, not a ladder of ternaries.
const OBSERVANCE_LABELS = {
  disha_shool: ["Disha Shool (avoid)", "दिशा शूल (बचें)"],
  rahu_vasa: ["Rahu Vasa", "राहु वास"],
  chandra_vasa: ["Chandra Vasa", "चंद्र वास"],
  agnivasa: ["Agnivasa", "अग्नि वास"],
  shivavasa: ["Shivavasa", "शिव वास"],
  anandadi_yoga: ["Anandadi Yoga", "आनंदादि योग"],
  day_length: ["Day length", "दिनमान"],
  night_length: ["Night length", "रात्रिमान"],
  madhyahna: ["Madhyahna", "मध्याह्न"],
  pratah_sandhya: ["Pratah Sandhya", "प्रातः संध्या"],
  sayahna_sandhya: ["Sayahna Sandhya", "सायं संध्या"],
  godhuli: ["Godhuli", "गोधूलि"],
  vijaya_muhurta: ["Vijaya Muhurta", "विजय मुहूर्त"],
  nishita_muhurta: ["Nishita Muhurta", "निशीथ मुहूर्त"],
} as const satisfies Record<string, readonly [string, string]>;

type ObservanceKey = keyof typeof OBSERVANCE_LABELS;

function observanceLabel(key: ObservanceKey, isHindi: boolean): string {
  return OBSERVANCE_LABELS[key][isHindi ? 1 : 0];
}

/** A verdict that carries an auspicious flag renders a check when it is good. */
function verdict(value: string, auspicious: boolean): string {
  return auspicious ? `${value} \u2713` : value;
}

function ObservancesPanel({
  observances,
  isHindi,
}: {
  observances: NonNullable<PanchangResponse["observances"]>;
  isHindi: boolean;
}) {
  const facts: Array<[ObservanceKey, string]> = [
    ["disha_shool", observances.disha_shool],
    ["rahu_vasa", observances.rahu_vasa],
    ["chandra_vasa", observances.chandra_vasa],
    ["agnivasa", verdict(observances.agnivasa, observances.agnivasa_auspicious)],
    ["shivavasa", verdict(observances.shivavasa, observances.shivavasa_auspicious)],
    ["anandadi_yoga", observances.anandadi_yoga.name],
    ["day_length", `${observances.day_length_hours.toFixed(2)} h`],
    ["night_length", `${observances.night_length_hours.toFixed(2)} h`],
  ];

  const windows: Array<[ObservanceKey, { start: string; end: string }]> = [
    ["madhyahna", observances.madhyahna],
    ["pratah_sandhya", observances.pratah_sandhya],
    ["sayahna_sandhya", observances.sayahna_sandhya],
    ["godhuli", observances.godhuli],
    ["vijaya_muhurta", observances.vijaya_muhurta],
    ["nishita_muhurta", observances.nishita_muhurta],
  ];

  return (
    <DepthSection title={isHindi ? "दिशा, वास और काल विभाग" : "Direction, vasa and day divisions"}>
      <div className="mb-4">
        {facts.map(([key, value]) => (
          <FactRow key={key} label={observanceLabel(key, isHindi)} value={value} />
        ))}
      </div>
      <div>
        {windows.map(([key, window]) => (
          <FactRow
            key={key}
            label={observanceLabel(key, isHindi)}
            value={`${window.start} \u2013 ${window.end}`}
          />
        ))}
      </div>
    </DepthSection>
  );
}

function BalaPanel({
  bala,
  isHindi,
}: {
  bala: NonNullable<PanchangResponse["bala"]>;
  isHindi: boolean;
}) {
  const byQuality = (quality: Quality) => ({
    nakshatras: bala.tarabalam.filter((row) => row.quality === quality),
    rashis: bala.chandrabalam.filter((row) => row.quality === quality),
  });

  return (
    <DepthSection
      title={isHindi ? "ताराबल और चंद्रबल" : "Tarabalam & Chandrabalam"}
      subtitle={
        isHindi
          ? `${bala.favourable_nakshatras.length} नक्षत्र अनुकूल`
          : `${bala.favourable_nakshatras.length} nakshatras favourable`
      }
    >
      <p className="mb-4 text-sm text-ish-faint">
        {isHindi
          ? "अपना जन्म नक्षत्र और राशि नीचे देखें।"
          : "Find your own janma nakshatra and rashi below."}
      </p>

      <FactRow
        label={isHindi ? "पंचक" : "Panchaka"}
        value={isHindi ? bala.panchaka.panchaka_hi : bala.panchaka.panchaka}
      />

      {QUALITY_ORDER.map((quality) => {
        const { nakshatras, rashis } = byQuality(quality);
        if (nakshatras.length === 0 && rashis.length === 0) return null;
        const heading = isHindi ? QUALITY_LABEL[quality].hi : QUALITY_LABEL[quality].en;
        return (
          <div key={quality} className="mt-4">
            <h3 className="mb-2 text-sm font-semibold text-ish-dark">{heading}</h3>
            {nakshatras.length > 0 && (
              <p className="mb-1 text-sm text-ish-faint">
                <span className="font-medium text-ish-dark">
                  {isHindi ? "नक्षत्र" : "Nakshatra"}:{" "}
                </span>
                {nakshatras.map((row) => `${row.nakshatra} (${row.tara})`).join(", ")}
              </p>
            )}
            {rashis.length > 0 && (
              <p className="text-sm text-ish-faint">
                <span className="font-medium text-ish-dark">{isHindi ? "राशि" : "Rashi"}: </span>
                {rashis.map((row) => row.rashi).join(", ")}
              </p>
            )}
          </div>
        );
      })}
    </DepthSection>
  );
}

export default function PanchangDepthPanels({
  result,
  isHindi,
}: {
  result: PanchangResponse;
  isHindi: boolean;
}) {
  // Every block is optional on the wire so a cached pre-deploy payload still
  // renders; absence must degrade to nothing, never to a crash.
  const { calendar_systems: systems, observances, bala } = result;
  if (!systems && !observances && !bala) return null;

  return (
    <div className="space-y-3" data-testid="panchang-depth-panels">
      {systems && <CalendarSystemsPanel systems={systems} isHindi={isHindi} />}
      {observances && <ObservancesPanel observances={observances} isHindi={isHindi} />}
      {bala && <BalaPanel bala={bala} isHindi={isHindi} />}
    </div>
  );
}
